import React, { useEffect, useRef } from "react";
import { RefreshCw, Loader2 } from "lucide-react";

// Editable draft of the proposal email. `version` bumps whenever the server re-renders the draft.
export function ProposalEditor({ subject, onSubject, html, version, onChange, edited, onRegenerate, loading }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = html || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  return (
    <div className="card-flat p-5" data-testid="proposal-summary">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Proposal summary — draft email to client</div>
          <div className="text-xs text-[var(--ink-2)] mt-0.5">Built from the <b>Proposal</b> template. Click into the text to edit before sending.</div>
        </div>
        <button data-testid="proposal-regenerate" onClick={onRegenerate} disabled={loading} className="btn-ghost flex items-center gap-1.5 text-xs !py-1.5 !px-2.5">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerate from template
        </button>
      </div>
      {edited && (
        <div className="text-[11px] mb-2 px-2 py-1 rounded-sm border border-[var(--gold)] bg-[var(--gold-soft)] text-[var(--warn)]" data-testid="proposal-edited-note">
          You have edited this draft. If you change services or fees, click Regenerate to refresh it.
        </div>
      )}
      <label className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-0.5">Subject</label>
      <input data-testid="proposal-subject" value={subject} onChange={(e) => onSubject(e.target.value)} className="input text-sm w-full mb-3" />
      <div
        ref={ref}
        data-testid="proposal-body"
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current.innerHTML)}
        className="proposal-body text-sm leading-relaxed border border-[var(--line)] rounded-sm p-5 bg-white max-h-[560px] overflow-y-auto outline-none focus:border-[var(--navy)]"
      />
      {!html && !loading && <div className="text-xs text-[var(--ink-2)] mt-2">No draft yet — click Regenerate.</div>}
    </div>
  );
}

export function AccountingFees({ annual, monthly, onChange }) {
  const num = (v) => (v === "" ? "" : Number(v));
  const round = (v) => Math.round(v * 100) / 100;
  return (
    <div className="mt-3 pt-3 border-t border-[var(--line)] flex items-end gap-4 flex-wrap" data-testid="accounting-fees">
      <div className="text-xs text-[var(--ink-2)] flex-1 min-w-[200px]">
        Single all-inclusive fee for the accounting services ticked above. Monthly is collected by Direct Debit (annual ÷ 12).
      </div>
      <FeeBox label="Annual Fee" testid="acct-annual-fee" value={annual}
        onChange={(v) => onChange({ annual: num(v), monthly: v === "" ? "" : round(Number(v) / 12) })} />
      <FeeBox label="Monthly Fee" testid="acct-monthly-fee" value={monthly}
        onChange={(v) => onChange({ monthly: num(v), annual: v === "" ? "" : round(Number(v) * 12) })} />
    </div>
  );
}

function FeeBox({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-0.5">{label}</label>
      <div className="flex items-center gap-1">
        <span className="text-xs text-[var(--ink-2)]">£</span>
        <input data-testid={testid} type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00" className="input text-sm !w-28 text-right !py-1.5" />
      </div>
    </div>
  );
}
